import { site } from '../content/site.js';

function escapeHtml(str) {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function renderLinkTile({ label, href, desc }) {
  const descHtml = desc
    ? `<span class="link-tile__desc">${escapeHtml(desc)}</span>`
    : '';

  return `
    <a href="${escapeHtml(href)}" class="link-tile" target="_blank" rel="noopener noreferrer">
      <span class="link-tile__label">${escapeHtml(label)}</span>
      ${descHtml}
    </a>
  `;
}

function renderSoonTile({ label, desc, popupId }) {
  const descHtml = desc
    ? `<span class="link-tile__desc">${escapeHtml(desc)}</span>`
    : '';

  return `
    <button type="button" class="link-tile link-tile--soon" data-stones-popup="${escapeHtml(popupId)}" aria-haspopup="dialog">
      <span class="link-tile__label">${escapeHtml(label)}</span>
      ${descHtml}
      <span class="link-tile__badge">Coming soon</span>
    </button>
  `;
}

function renderListenPlatforms(links, platforms) {
  return platforms
    .map(({ label, key }) => renderLinkTile({ label, href: links[key] }))
    .join('\n');
}

function renderFooterGroups(links, groups) {
  return groups
    .map(
      (group) => `
        <div class="footer__group">
          <p class="footer__group-label">${escapeHtml(group.label)}</p>
          <div class="footer__group-links">
            ${group.links
              .map(
                ({ label, key }) =>
                  `<a href="${escapeHtml(links[key])}" target="_blank" rel="noopener noreferrer">${escapeHtml(label)}</a>`,
              )
              .join('\n')}
          </div>
        </div>
      `,
    )
    .join('\n');
}

function renderCard(card) {
  if (card.opensDialog === 'stones') {
    return `
      <button type="button" class="card card--${card.id}" data-stones-popup="stonesDialog" aria-haspopup="dialog">
        <span class="card__label">${escapeHtml(card.label)}</span>
        <span class="card__desc">${escapeHtml(card.desc)}</span>
      </button>
    `;
  }

  const attrs = card.external
    ? ' target="_blank" rel="noopener noreferrer"'
    : '';

  return `
    <a href="${escapeHtml(card.href)}" class="card card--${card.id}"${attrs}>
      <span class="card__label">${escapeHtml(card.label)}</span>
      <span class="card__desc">${escapeHtml(card.desc)}</span>
    </a>
  `;
}

export function renderApp(root) {
  const { links, listen, story, stones, footer } = site;

  root.innerHTML = `
    <div class="ambient-bg" aria-hidden="true">
      <canvas id="particles"></canvas>
      <div class="glow glow--1"></div>
      <div class="glow glow--2"></div>
      <div class="glow glow--3"></div>
      <div class="glow glow--4"></div>
      <div class="ambient-bg__vignette"></div>
      <div class="ambient-bg__scanlines"></div>
    </div>

    <div class="audio-controls" id="audioControls">
      <p class="audio-hint" id="audioHint" role="status" aria-live="polite">
        ${escapeHtml(site.audio.trackName)} · ${escapeHtml(site.audio.hint)}
      </p>
      <button class="audio-toggle audio-toggle--idle" id="audioToggle" aria-label="Unmute ${escapeHtml(site.audio.trackName)}" aria-pressed="false">
        <svg class="audio-toggle__icon audio-toggle__icon--off" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true">
          <path d="M11 5L6 9H2v6h4l5 4V5z"/>
          <line x1="23" y1="9" x2="17" y2="15"/>
          <line x1="17" y1="9" x2="23" y2="15"/>
        </svg>
        <svg class="audio-toggle__icon audio-toggle__icon--on" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true">
          <path d="M11 5L6 9H2v6h4l5 4V5z"/>
          <path d="M19.07 4.93a10 10 0 0 1 0 14.14"/>
          <path d="M15.54 8.46a5 5 0 0 1 0 7.07"/>
        </svg>
      </button>
    </div>

    <main class="page">
      <section class="hero" id="top">
        <header class="hero__header fade-in fade-in--1">
          <h1 class="hero__title">${escapeHtml(site.title)}</h1>
          <p class="hero__tagline">${escapeHtml(site.tagline)}</p>
        </header>

        <nav class="cards fade-in fade-in--2" aria-label="Site sections">
          ${site.cards.map(renderCard).join('\n')}
        </nav>
      </section>

      <section class="listen section-panel" id="listen">
        <div class="section-panel__inner fade-in-scroll">
          <h2 class="section-panel__heading">${escapeHtml(listen.heading)}</h2>
          <p class="section-panel__intro">${escapeHtml(listen.intro)}</p>
          <div class="link-grid" aria-label="Streaming platforms">
            ${renderListenPlatforms(links, site.listenPlatforms)}
          </div>
        </div>
      </section>

      <section class="story section-panel" id="story">
        <div class="section-panel__inner section-panel__inner--prose fade-in-scroll">
          <h2 class="section-panel__heading">${escapeHtml(story.heading)}</h2>
          <div class="story__body">
            ${story.paragraphs.map((p) => `<p>${escapeHtml(p)}</p>`).join('\n')}
          </div>
        </div>
      </section>

      <section class="stones section-panel" id="stones">
        <div class="section-panel__inner fade-in-scroll">
          <h2 class="section-panel__heading">${escapeHtml(stones.heading)}</h2>
          <p class="section-panel__intro">${escapeHtml(stones.text)}</p>
          <div class="link-grid link-grid--compact" aria-label="Shop">
            ${links.etsy
              ? renderLinkTile({ label: stones.etsy.label, href: links.etsy, desc: stones.etsy.desc })
              : renderSoonTile({ label: stones.etsy.label, desc: stones.etsy.desc, popupId: 'stonesDialog' })}
          </div>
          <p class="stones__contact">
            Questions or custom requests —
            <a href="mailto:${escapeHtml(site.email)}" class="stones__link">${escapeHtml(site.email)}</a>
          </p>
        </div>
      </section>

      <dialog class="stones-dialog" id="stonesDialog" aria-labelledby="stonesDialogTitle">
        <div class="stones-dialog__panel">
          <h3 class="stones-dialog__title" id="stonesDialogTitle">${escapeHtml(stones.etsy.label)}</h3>
          <p class="stones-dialog__text">${escapeHtml(stones.etsy.comingSoonMessage)}</p>
          <button type="button" class="stones-dialog__close" data-stones-close>${escapeHtml(stones.etsy.dismissLabel)}</button>
        </div>
      </dialog>

      <footer class="footer">
        <p class="footer__name">${escapeHtml(site.title)}</p>
        <nav class="footer__groups" aria-label="External links">
          ${renderFooterGroups(links, site.footerGroups)}
        </nav>
        <p class="footer__bio">${escapeHtml(footer.bio)}</p>
      </footer>
    </main>
  `;
}
